import React from 'react';
import { useIdleTimer } from 'react-idle-timer';

export interface UseIdleOptions {
  // How long (ms) of inactivity before the user is considered idle
  idleTimeout: number;
  // How long (ms) to show a warning
  idleWarningTimeout: number;
  // Called when the warning period expires
  onIdle: () => void;
  enabled: boolean;
}

/**
 * Tracks user inactivity with a two-phase idle flow: active → warning → idle.
 * Cross-tab aware throughout, including during the warning phase.
 */
export const useIdle = (options: UseIdleOptions) => {
  const [isIdleWarning, setIsIdleWarning] = React.useState(false);
  const [idleWarningRemaining, setIdleWarningRemaining] = React.useState(0);

  const { activate, getRemainingTime } = useIdleTimer({
    timeout: options.idleTimeout + options.idleWarningTimeout,
    promptBeforeIdle: options.idleWarningTimeout,
    disabled: !options.enabled,
    crossTab: true,
    syncTimers: 200,
    throttle: 500,
    onPrompt: () => setIsIdleWarning(true),
    onIdle: () => {
      setIsIdleWarning(false);
      options.onIdle();
    },
    onActive: () => setIsIdleWarning(false),
  });

  const dismissIdleWarning = React.useCallback(() => {
    setIsIdleWarning(false);
    activate();
  }, [activate]);

  // Poll remaining time so consumers can display a countdown
  React.useEffect(() => {
    const interval = setInterval(() => {
      setIdleWarningRemaining(Math.ceil(getRemainingTime() / 1000));
    }, 500);

    return () => clearInterval(interval);
  });

  return {
    isIdleWarning,
    idleWarningRemaining,
    dismissIdleWarning,
  };
};
